/**
 * "Add condition" row: lists dimensions not already in the editor and adds
 * the picked one with a sensible default predicate.
 */

import { useState, type FC } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';

import { CONDITION_REGISTRY } from './model';

export interface ConditionsEditorAddRowProps {
  takenKeys: readonly string[];
  onAdd: (key: string) => void;
}

const ConditionsEditorAddRow: FC<ConditionsEditorAddRowProps> = ({
  takenKeys,
  onAdd,
}) => {
  const [key, setKey] = useState('');
  const taken = new Set(takenKeys);
  const options = CONDITION_REGISTRY.filter(
    (d) => d.authorable && !taken.has(d.key),
  ).sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
  );

  const handleAdd = () => {
    if (!key) return;
    onAdd(key);
    setKey('');
  };

  return (
    <div className="flex items-center gap-1.5 rounded-md border border-dashed border-border/60 bg-muted/5 px-2 py-1.5">
      <select
        aria-label="Pick a condition to add"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        disabled={options.length === 0}
        className="h-7 min-w-0 flex-1 rounded border border-border/60 bg-background px-1.5 text-[11px] text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-60"
      >
        <option value="">Select condition</option>
        {options.map((opt) => (
          <option key={opt.key} value={opt.key}>
            {opt.label}
          </option>
        ))}
      </select>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={handleAdd}
        disabled={!key}
        className="h-7 shrink-0 gap-1 px-2 text-[11px]"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
        <span>Add condition</span>
      </Button>
    </div>
  );
};

export default ConditionsEditorAddRow;
